import Vue from "vue";
import _ from "lodash";

import bridge from "@/api/bridge";
import cloud from "@/api/cloud";

import { DEVICE_CLASS_ALEXA } from "@/utils/k4util";

const getInitState = () => ({
	tests: {},
});

export default {
	namespaced: true,
	state: getInitState(),

	getters: {
		// Whether or not a system requires the provisioning step
		requiresProvisioning: (state, getters, rootState, rootGetters) => {
			// Currently rooms containing only alexa devices don't require
			// provisioning. This is a very specific check, though, and may
			// need to be generalized further in the future as more devices
			// with third-party integrations are added.
			const devices = rootGetters["devices/devices"];
			return !_.every(devices, device =>
				_.startsWith(device.cls, DEVICE_CLASS_ALEXA)
			);
		},

		allTests: state => _.flatten(_.values(state.tests)),

		allTestsPass: (state, getters) =>
			_.every(_.map(getters.allTests, "success")),

		tests: state => guid => _.get(state.tests, guid, []),

		// Returns whether all tests have been run successfully. Will return false
		// if there are tests that haven't been run or if there are any errors.
		tested: (state, getters) => guid => {
			const tests = getters.tests(guid);
			return _.every(tests, { success: true });
		},

		hasError: (state, getters) => guid => {
			const tests = getters.tests(guid);
			return _.find(tests, test => test.error);
		},

		testing: (state, getters) => guid =>
			!!_.find(getters.tests(guid), { testing: true }),
	},

	mutations: {
		setTests(state, tests) {
			state.tests = tests;
		},

		setTestProp(state, { guid, idx, key, val }) {
			Vue.set(state.tests[guid][idx], key, val);
		},

		resetState(state) {
			Object.assign(state, getInitState());
		},
	},

	actions: {
		async readQR({ commit }) {
			const serial = await bridge.barcode();
			if (serial) {
				const { community, system } = await cloud.getBySerial(serial);
				this._vm.$log(
					"Read QR Code: " +
						JSON.stringify({
							serial,
							community: community.name,
							system: system.name,
						})
				);

				return { community, system };
			}
		},

		async loadTests({ commit, dispatch, rootGetters }) {
			const tests = {};
			const devices = rootGetters["devices/devices"];
			for (const device of devices) {
				try {
					tests[device.guid] = await dispatch("loadDeviceTests", device);
				} catch (e) {
					this._vm.$log(`Error loading tests for device ${device.name}: ${e}`);
				}
			}
			commit("setTests", tests);
		},

		async loadDeviceTests({ commit, dispatch, getters }, item) {
			let tests = _.map(
				await dispatch("wizard/getTests", item, { root: true }),
				test => ({
					// Add some props of our own
					...test,
					testing: false,
					success: false,
					error: null,
				})
			);
			return tests;
		},

		async submitReport({ getters, rootState, rootGetters }) {
			const systemId = rootState.session.systemId;
			const installer = rootGetters["auth/fullName"];

			const allTests = getters.allTests;
			const tests = _.reduce(
				_.groupBy(allTests, "device.guid"),
				(accum, tests, guid) => {
					const device = rootGetters["devices/itemByGuid"](guid);
					accum[guid] = {
						name: device.name,
						cls: device.cls,
						path: device.path,
						passed: _.map(tests, test => ({
							type: test.type,
							path: test.path,
						})),
					};
					return accum;
				},
				{}
			);

			await cloud.submitReport(systemId, {
				date: Date.now(),
				installer,
				tests,
			});
		},
	},
};
